import { useState } from 'react';
import { FileEdit as Edit3, AlertTriangle, Save, Settings, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToastContext } from '../contexts/ToastContext';
import { calculateDaysDifference, formatDate } from '../utils/dateUtils';
import { LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import StampCalendar from '../components/StampCalendar';
import FlowerGrowthAnimation from '../components/FlowerGrowthAnimation';
import FlowerBloomPopup from '../components/FlowerBloomPopup';
import Navigation from '../components/Navigation';
import ManualLink from '../components/ManualLink';
import './MyPageScreen.css';

export default function MyPageScreen() {
  const { currentUser, updateUserStamps, updateUserProfile, updateUserTheme, loading } = useAuth();
  const { showSuccess, showError } = useToastContext();
  const [showFlowerPopup, setShowFlowerPopup] = useState(false);
  const [newFlowerCount, setNewFlowerCount] = useState(0);
  const [showThemeEditModal, setShowThemeEditModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState('');
  const [isSavingTheme, setIsSavingTheme] = useState(false);
  const [showPRModal, setShowPRModal] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [businessContent, setBusinessContent] = useState('');
  const [isSavingPR, setIsSavingPR] = useState(false);

  const { logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="mypage-container">
        <div className="mypage-header">
          <h1 className="mypage-title">マイページ</h1>
        </div>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="mypage-container">
        <div className="mypage-header">
          <h1 className="mypage-title">マイページ</h1>
        </div>
        <p>ログインしてください</p>
      </div>
    );
  }

  const daysSinceStart = calculateDaysDifference(currentUser.themeStartDate);

  const handleStamp = (date: string) => {
    // 日本時間で今日の日付を取得
    // const now = new Date();
    // const jstOffset = 9 * 60; // JST is UTC+9
    // const jstDate = new Date(now.getTime() + jstOffset * 60 * 1000);
    // const todayJST = jstDate.toISOString().split('T')[0]; // YYYY-MM-DD format in JST

    const newStamps = [...currentUser.stamps];
    if (!newStamps.includes(date)) {
      newStamps.push(date);
      updateUserStamps(currentUser.id, newStamps);

      // 新しい花の数を設定してポップアップを表示
      setNewFlowerCount(newStamps.length);
      setShowFlowerPopup(true);
    }
  };

  const handleClosePopup = () => {
    setShowFlowerPopup(false);
  };

  const handlePhotoUpdate = (photoUrl: string) => {
    if (currentUser) {
      updateUserProfile(currentUser.id, { profilePhoto: photoUrl });
    }
  };

  const handleEditTheme = () => {
    setEditingTheme(currentUser.theme);
    setShowThemeEditModal(true);
  };

  const handleCancelThemeEdit = () => {
    setShowThemeEditModal(false);
    setEditingTheme('');
  };

  const handleSaveTheme = async () => {
    if (!editingTheme.trim()) {
      showError('テーマを入力してください');
      return;
    }

    if (editingTheme.trim() === currentUser.theme) {
      setShowThemeEditModal(false);
      return;
    }

    setIsSavingTheme(true);
    try {
      const today = formatDate(new Date());
      await updateUserTheme(currentUser.id, editingTheme.trim(), today);
      setShowThemeEditModal(false);
      setEditingTheme('');
      showSuccess('テーマを更新しました！新しいチャレンジが始まります。');
    } catch (error) {
      console.error('Theme update failed:', error);
      showError('テーマの更新に失敗しました。もう一度お試しください。');
    } finally {
      setIsSavingTheme(false);
    }
  };

  const handleOpenPRModal = () => {
    setCompanyName(currentUser.companyName || '');
    setBusinessContent(currentUser.businessContent || '');
    setShowPRModal(true);
  };

  const handleClosePRModal = () => {
    setShowPRModal(false);
    setCompanyName('');
    setBusinessContent('');
  };

  const handleSavePR = async () => {
    setIsSavingPR(true);
    try {
      await updateUserProfile(currentUser.id, {
        companyName: companyName.trim() || undefined,
        businessContent: businessContent.trim() || undefined
      });
      setShowPRModal(false);
      showSuccess('PR設定を更新しました！');
    } catch (error) {
      console.error('PR settings update failed:', error);
      showError('PR設定の更新に失敗しました。もう一度お試しください。');
    } finally {
      setIsSavingPR(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="mypage-container">
      <div className="mypage-header">
        <div className="header-logo">
          <img src="/images/100-day_logo-20251014.png" alt="100日実践" className="header-logo-image" />
        </div>
        <h1 className="mypage-title">マイページ</h1>
        <div className="header-spacer"></div>
      </div>


      <div className="manual-link-container-logged-in">
        <ManualLink />
      </div>


      <button className="pr-settings-button" onClick={handleOpenPRModal}>
        <Settings size={20} />
      </button>

      <div className="mypage-content">
        <div className="pr-settings-section" style={{ display: 'none'}}>
          <button className="pr-settings-button" onClick={handleOpenPRModal}>
            <Settings size={20} />
          </button>
        </div>

        <div className="profile-section">
          <ProfilePhotoUpload
            currentPhotoUrl={currentUser.profilePhoto}
            onPhotoUpdate={handlePhotoUpdate}
          />
          <h2 className="user-name">{currentUser.name}</h2>
          {currentUser.organizationName && (
            <p className="phone">{currentUser.organizationName}</p>
          )}
          {currentUser.organizationName2 && (
            <p className="phone">{currentUser.organizationName2}</p>
          )}
          <p className="days-count">{daysSinceStart}日目</p>
        </div>

        <div className="theme-section">
          <h3 className="section-title">あなたのテーマ</h3>
          <div className="theme-container">
            <p className="theme">{currentUser.theme}</p>
            <p className="start-date">開始日: {currentUser.themeStartDate}</p>
            <button className="edit-theme-button" onClick={handleEditTheme}>
              <Edit3 size={16} />
              テーマを編集
            </button>
          </div>
        </div>

        <div className="flower-section">
          <h3 className="section-title">あなたの成長</h3>
          <p className="flower-description">
            スタンプを押すたびに<br/>実践の花が育ちます
          </p>
          <FlowerGrowthAnimation 
            stampCount={currentUser.stamps.length}
            maxStamps={100}
          />
        </div>

        <div className="calendar-section">
          <h3 className="section-title">スタンプカレンダー</h3>
          <p className="calendar-description">
            当日または昨日の日付をタップして<br/>スタンプを押しましょう
          </p>
          <div className="calendar-container">
            <StampCalendar
              startDate={currentUser.themeStartDate}
              stamps={currentUser.stamps}
              onStamp={handleStamp}
              isReadOnly={false}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'right', paddingRight: '20px' }}>
          <button className="logout-button" style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={handleLogout}>
            <LogOut size={24} /><span style={{ fontSize: '20px', position: 'relative', top: '1px' }}>ログアウト</span>
          </button>
        </div>

        <div className="mypage-footer">
          <p className="copyright">© 株式会社カフ．ウェブ．システム</p>
        </div>
      </div>

      <Navigation />
      
      <FlowerBloomPopup
        isVisible={showFlowerPopup}
        onClose={handleClosePopup}
        flowerCount={newFlowerCount}
      />

      {/* PR設定モーダル */}
      {showPRModal && (
        <div className="pr-modal-overlay" onClick={handleClosePRModal}>
          <div className="pr-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pr-modal-header">
              <h3>
                <Settings size={20} />
                PR設定
              </h3>
              <button className="pr-close-button" onClick={handleClosePRModal}>
                <X size={24} />
              </button>
            </div>
            
            <div className="pr-modal-body">
              <div className="pr-input-container">
                <label className="pr-input-label">会社名</label>
                <input
                  type="text"
                  className="pr-input-field"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="例：株式会社○○"
                  maxLength={100}
                />
                <div className="pr-char-count">
                  {companyName.length}/100文字
                </div>
              </div>

              <div className="pr-input-container">
                <label className="pr-input-label">事業内容</label>
                <textarea
                  className="pr-input-field pr-textarea-field"
                  value={businessContent}
                  onChange={(e) => setBusinessContent(e.target.value)}
                  placeholder="例：Webシステム開発、アプリ開発、ITコンサルティング"
                  maxLength={500}
                  rows={4}
                />
                <div className="pr-char-count">
                  {businessContent.length}/500文字
                </div>
              </div>

              {/* <NotificationSetup /> */} 
            </div>
            
            <div className="pr-modal-footer">
              <button 
                className="pr-cancel-button" 
                onClick={handleClosePRModal}
                disabled={isSavingPR}
              >
                キャンセル
              </button>
              <button 
                className="pr-save-button" 
                onClick={handleSavePR}
                disabled={isSavingPR}
              >
                {isSavingPR ? (
                  <>
                    <Save size={16} />
                    保存中...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    保存
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* テーマ編集モーダル */}
      {showThemeEditModal && (
        <div className="theme-edit-modal-overlay" onClick={handleCancelThemeEdit}>
          <div className="theme-edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="theme-modal-header">
              <h3>テーマを編集</h3>
              <button className="theme-close-button" onClick={handleCancelThemeEdit}>
                ×
              </button>
            </div>
            
            <div className="theme-modal-body">
              <div className="warning-message">
                <div className="warning-title">
                  <AlertTriangle size={20} />
                  重要な注意事項
                </div>
                <p className="warning-text">
                  テーマを変更すると、現在のスタンプがすべてリセットされ、今日から新しい100日実践が始まります。この操作は取り消せません。
                </p>
              </div>
              
              <div className="theme-input-container">
                <label className="theme-input-label">新しいテーマ</label>
                <textarea
                  className="theme-input-field"
                  value={editingTheme}
                  onChange={(e) => setEditingTheme(e.target.value)}
                  placeholder="例：毎日30分ウォーキングをする"
                  maxLength={200}
                />
              </div>
            </div>
            
            <div className="theme-modal-footer">
              <button 
                className="theme-cancel-button" 
                onClick={handleCancelThemeEdit}
                disabled={isSavingTheme}
              >
                キャンセル
              </button>
              <button 
                className="theme-save-button" 
                onClick={handleSaveTheme}
                disabled={isSavingTheme}
              >
                {isSavingTheme ? (
                  <>
                    <Save size={16} />
                    保存中...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    保存
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}